import * as React from 'react';
import { AlertMessage, AlertType, Form, SpinnerWrapper, HiddenInput } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { useParams } from 'react-router-dom';
import { setMessage } from '../../../helpers/MessageHelper';
import { GeneralDentalReferralApi } from '../../../api/GeneralDentalReferralApi';
import { GeneralDentalReferralSections } from './GeneralDentalReferralSections/GeneralDentalReferralSections';
import { IGeneralDentalReferral } from '../../../models/IGeneralDentalReferral';
import { IPatient } from '../../../models/IPatient';
import { GeneralDentalReferralFooterButtons } from './GeneralDentalReferralButtons/GeneralDentalReferralFooterButtons';
import { UserContext, isInRole } from '../../../contexts/UserContext';
import { TANDREGLERINGBAS } from '../../../utils/RoleConstants';
import { ICareProvider } from '../../../models/ICareProvider';
import './GeneralDentalReferral.scss';
import { EventLog } from "../../../components/EventLog/EventLog";

interface IGeneralDentalReferralView {
    patient: IPatient;
}

export const GeneralDentalReferral: React.FunctionComponent<IGeneralDentalReferralView> = ({ patient }) => {
    const auth = useAuth();
    const api = new GeneralDentalReferralApi(auth);
    const { id } = useParams<{id: string}>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [isFetchingGeneralDentalReferral, setIsFetchingGeneralDentalReferral] = React.useState<boolean>(false);
    const userInfo = React.useContext(UserContext);
    const hasRoleOrtodontist = isInRole(TANDREGLERINGBAS, userInfo.roles);
    const [generalDentalReferral, setGeneralDentalReferral] = React.useState<IGeneralDentalReferral>();

    React.useEffect(() => {
        if (id) {
            getGeneralDentalReferral(parseInt(id), false);
        }
        else {
            setIsFetchingGeneralDentalReferral(true);

            var referrerCareProvider: ICareProvider,
                recipientCareProvider: ICareProvider;

            api.getGeneralDentalReferralReferrer((careProvider) => {
                referrerCareProvider = careProvider;
            }, undefined, () => {
                api.getGeneralDentalReferralRecipient(patient.patientId, (careProvider) => {
                    recipientCareProvider = careProvider;
                    setGeneralDentalReferral({ canEdit: hasRoleOrtodontist, recipientCareProvider: recipientCareProvider, referrerCareProvider: referrerCareProvider });
                }, undefined, () => setIsFetchingGeneralDentalReferral(false));
            });
        }
    }, [])

    const getGeneralDentalReferral = (generalDentalReferralId: number, refresh: boolean) => {
        setIsFetchingGeneralDentalReferral(true);
        api.getGeneralDentalReferral(generalDentalReferralId, refresh
            , (data) => { setGeneralDentalReferral(data) }
            , (error) => changeErrorMessage(error)
            , () => setIsFetchingGeneralDentalReferral(false))
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage ?? "");
        setSuccessMessage(newMessage);
    }

    const changeErrorMessage = (message: string) => {
        const newMessage = setMessage(message, errorMessage ?? "");
        setErrorMessage(newMessage);
    }

    return (
        <SpinnerWrapper isLoading={isFetchingGeneralDentalReferral}>
            <Form>
                <HiddenInput name="generalDentalReferralId" value={generalDentalReferral?.generalDentalReferralId?.toString() ?? "0"} />
                <section className="card">
                    <div className="card-header">
                        <div className="row general-dental-referral-header-row">
                            <div className="col">
                                <h1>Allmäntandvårdsremiss {generalDentalReferral?.generalDentalReferralNumber && generalDentalReferral?.generalDentalReferralNumber !== "" ? "- " + generalDentalReferral?.generalDentalReferralNumber : ""}</h1>
                            </div>
                            {
                                generalDentalReferral?.unreadAnswer ?
                                    <div className="col">
                                        <h2 className="text-center">*** Oläst ***</h2>
                                    </div>
                                    : null
                            }
                            <div className="col"></div>
                        </div>
                    </div>
                    <div className="card-body">
                        <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                        <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                        {
                            generalDentalReferral ?
                                <>
                                    <GeneralDentalReferralSections 
                                        generalDentalReferral={generalDentalReferral} 
                                        generalDentalReferralId={generalDentalReferral.generalDentalReferralId} 
                                        patient={patient} 
                                        isFetchingGeneralDentalReferral={isFetchingGeneralDentalReferral} 
                                        changeSuccessMessage={changeSuccessMessage} 
                                        changeErrorMessage={changeErrorMessage} 
                                        getGeneralDentalReferral={getGeneralDentalReferral} />
                                    {
                                        generalDentalReferral?.events ?
                                            <>
                                                <hr />
                                                <EventLog id="referralEventLog" events={generalDentalReferral.events} />
                                            </>
                                            : null
                                    }
                                </>
                                : null
                        }
                    </div>
                    <div className="card-footer">
                        {
                            generalDentalReferral ?
                                <GeneralDentalReferralFooterButtons 
                                    changeSuccessMessage={changeSuccessMessage}
                                    changeErrorMessage={changeErrorMessage}
                                    canEdit={generalDentalReferral.canEdit}
                                    canWithdraw={generalDentalReferral.canWithdraw ?? false}
                                    canReject={generalDentalReferral.canReject ?? false}
                                    canApprove={generalDentalReferral.canApprove ?? false}
                                    canWriteAnswer={generalDentalReferral.canWriteAnswer ?? false}
                                    canRequestCompletion={generalDentalReferral.canRequestCompletion ?? false}
                                    hasUnreadAnswer={generalDentalReferral.unreadAnswer ?? false}
                                    getGeneralDentalReferral={(id) => getGeneralDentalReferral(id, true)}
                                    generalDentalReferralId={generalDentalReferral.generalDentalReferralId} />
                                : null
                        }
                    </div>
                </section>
            </Form>
        </SpinnerWrapper>
    )
}